// Which checkout the cart opens: today's drawer, or the full page in steps
// (checkoutLayout in @vertu/shared, owner 25 Sept). Judged from the booking
// forms' DEFINITIONS (get_cart_booking_participants: each hold's snapshot of
// its questions, its headcount, its statements) and each service's
// checkout_layout setting, never from an answer. CartPanel reads the forms
// while the guest looks at "Your visit", so the decision is ready when they
// press Continue, and freezes it for as long as the checkout is open.
import { useEffect, useState } from "react";
import {
  checkoutLayout,
  isPhoneViewport,
  isPhoneWidth,
  type CheckoutLayoutDecision,
  type CheckoutLayoutLine,
  type CheckoutLayoutSetting,
} from "@vertu/shared";
import type { CheckoutForm } from "../../state/booking-questions-form";
import { readCartForms } from "../ParticipantDetails";

/** The layout's view of the cart's forms: definitions only (nothing a guest typed). */
export function layoutLines(
  forms: readonly CheckoutForm[],
  settingOf: (serviceId: string) => CheckoutLayoutSetting | null | undefined,
): CheckoutLayoutLine[] {
  return forms.map((form) => ({
    setting: settingOf(form.service_id) ?? "auto",
    perPerson: form.fields.map((f) => ({ key: f.key, profile: f.profile ?? null })),
    party: form.party_size,
    visitKey: form.visit_key,
    groupQuestions: form.group_fields.length,
    statements: form.confirmations?.statements.length ?? 0,
  }));
}

export function decideCheckoutLayout(
  forms: readonly CheckoutForm[],
  settingOf: (serviceId: string) => CheckoutLayoutSetting | null | undefined,
  phone: boolean,
): CheckoutLayoutDecision {
  return checkoutLayout({ lines: layoutLines(forms, settingOf), phone });
}

/** Does any form ask each guest something (so a phone shows one card at a time)? */
export function asksPerPerson(forms: readonly CheckoutForm[]): boolean {
  return forms.some((f) => f.fields.length > 0);
}

export type CheckoutFormsProbe =
  | { status: "idle" | "loading"; forms: null }
  | { status: "ready"; forms: CheckoutForm[] }
  /** The read failed: the checkout opens as the drawer, as it always did. */
  | { status: "failed"; forms: null };

/**
 * How long the checkout waits for the forms before it opens as the drawer
 * anyway (the drawer reads them itself, as it always did): a slow read never
 * leaves the guest on "Getting your checkout ready…".
 */
export const FORMS_PROBE_TIMEOUT_MS = 8000;

/**
 * The cart's booking forms, read once per set of holds (the signature is
 * each hold's id and headcount). No holds: ready with none, no read at all
 * (a spa-only cart). A read slower than FORMS_PROBE_TIMEOUT_MS counts as
 * failed.
 */
export function useCheckoutFormsProbe(cartToken: string, signature: string, enabled: boolean): CheckoutFormsProbe {
  const [probe, setProbe] = useState<{ key: string; value: CheckoutFormsProbe }>({
    key: "",
    value: { status: "idle", forms: null },
  });
  const key = `${cartToken}#${signature}`;
  useEffect(() => {
    if (!enabled || !signature) return;
    let cancelled = false;
    setProbe({ key, value: { status: "loading", forms: null } });
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      cancelled = true;
      setProbe({ key, value: { status: "failed", forms: null } });
    }, FORMS_PROBE_TIMEOUT_MS);
    readCartForms(cartToken).then(
      (forms) => {
        window.clearTimeout(timer);
        if (!cancelled) setProbe({ key, value: { status: "ready", forms } });
      },
      () => {
        window.clearTimeout(timer);
        if (!cancelled) setProbe({ key, value: { status: "failed", forms: null } });
      },
    );
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [cartToken, signature, enabled, key]);
  if (!signature) return { status: "ready", forms: [] };
  if (!enabled || probe.key !== key) return { status: "loading", forms: null };
  return probe.value;
}

/**
 * Is the checkout on a phone? The checkout covers the widget's viewport
 * (position: fixed, inset 0), so its container is the viewport: 640px or
 * narrower, or a screen too short for the summary column beside the form (a
 * phone held sideways, 500px or shorter: isPhoneViewport). Live, so turning a
 * phone or tablet round changes how the cards are shown, never which layout
 * opened.
 */
export function usePhoneWidth(): boolean {
  return useViewportTest((w, h) => isPhoneViewport(w, h));
}

/**
 * Does the drawer cover the widget's header? Only where it is full-width:
 * 640px or narrower (isPhoneWidth; styles.css gives .vt-cart-panel 100vw
 * under the same max-width: 640px). A phone held sideways or a short laptop
 * window keeps the 420px side panel beside the header, so this is narrower
 * than usePhoneWidth, which also counts a short screen. Live.
 */
export function useDrawerCoversHeader(): boolean {
  return useViewportTest((w) => isPhoneWidth(w));
}

/** A test of the viewport's size, kept current as the window changes (false without one). */
function useViewportTest(test: (width: number, height: number) => boolean): boolean {
  const read = () => (typeof window === "undefined" ? false : test(window.innerWidth, window.innerHeight));
  const [value, setValue] = useState(read);
  useEffect(() => {
    const onResize = () => setValue(read());
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return value;
}
